import { useMenuDispatch, useMenuState } from '@olo-web/client-state';
import { useMenuFilteredBySearch } from '@olo-web/domain/menu/queries';
import { scrollElementToTop } from '@olo-web/utils/common/functions';
import { useScreenSizeState } from '@olo-web/client-state';

type TUseMenuGroupRibbon = {
  groupRibbonItems: IRibbonItem[];
  handleGroupSelect: (groupPreview: IRibbonItem) => void;
  handleMenuRibbonFocus: () => void;
  selectedGroupId: string | null;
  searchText: string | null;
  isLoading: boolean;
  isIdle: boolean;
};

export const useMenuGroupRibbon = (): TUseMenuGroupRibbon => {
  const menuDispatch = useMenuDispatch();
  const { groups, isLoading, isIdle } = useMenuFilteredBySearch();
  const { searchText, selectedGroupId, scrollBoxRef, menuRibbonRef } = useMenuState();
  const { isMdDown } = useScreenSizeState();
  // Pixels of sticky/fixed nav-bar covering the top of the scroll container.
  // Mobile: ScrollSensitiveNavBar height (98px) which embeds the ribbon when scrolled.
  // Desktop: sticky ribbon (top:63 + h:44 = 107) inside MenuHeader.
  const yOffset = isMdDown ? 98 : 107;

  const groupRibbonItems: IRibbonItem[] = groups.map((g) => ({ label: g?.name, value: g.id }));

  const handleGroupSelect = (groupPreview: IRibbonItem) => {
    menuDispatch({
      type: 'UPDATE_SELECTED_GROUP',
      payload: groupPreview.value,
    });
    const elem = document?.getElementById(`group-menu-${groupPreview.value}`);
    if (!elem) return;
    // Suppress useMenuGroupScrollListener's scroll-spy while the programmatic
    // smooth-scroll is in flight so it doesn't override the user's selection
    // with whichever section happens to cross the nav threshold mid-animation.
    menuDispatch({ type: 'SET_PROGRAMMATIC_SCROLLING', payload: true });
    scrollElementToTop({
      element: elem,
      offset: yOffset,
      rootElement: scrollBoxRef,
    });
    // Clear the suppression flag when the smooth scroll finishes. Long
    // distances on big menus can take >1s, so a fixed timeout is unreliable;
    // prefer the native `scrollend` event with a 2.5s safety fallback.
    const sc = scrollBoxRef?.current;
    let cleared = false;
    let fallbackTimer = 0;
    const clearFlag = () => {
      if (cleared) return;
      cleared = true;
      sc?.removeEventListener('scrollend', clearFlag);
      window.clearTimeout(fallbackTimer);
      menuDispatch({ type: 'SET_PROGRAMMATIC_SCROLLING', payload: false });
    };
    sc?.addEventListener('scrollend', clearFlag, { once: true });
    fallbackTimer = window.setTimeout(clearFlag, 2500);
    // scrollElementToTop uses smooth scrolling (and setTimeout(0) for nested roots); delay focus until after scroll starts
    window.setTimeout(() => {
      elem.focus({ preventScroll: true });
    }, 400);
  };

  const handleMenuRibbonFocus = () => {
    const ribbonElement = menuRibbonRef?.current;
    const firstSectionLink = ribbonElement?.querySelector('a[href^="#group-menu-"]');
    if (firstSectionLink instanceof HTMLElement) {
      firstSectionLink.focus({ preventScroll: true });
    }
  };

  return {
    groupRibbonItems,
    handleGroupSelect,
    selectedGroupId,
    searchText,
    isLoading,
    isIdle,
    handleMenuRibbonFocus,
  };
};
